/**
 * TransactionsTab.tsx
 *
 * Page component for managing and displaying transaction tabs and tables.
 */
import { Typography } from "@mui/material";
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import CircularScreenLoader from "../../components/CircularScreenLoader";
import ResponsiveTabs from "../../components/NeuronResponsiveTabs";
import { TransactionsTabKeys } from "../../constant/enum";
import { useIsMobile } from "../../constant/isMobile";
import { LABEL_TEXTS } from "../../constant/stringConstant";
import { trackTabChangeWithSentry } from "../../utils/sentryTabTracker";

const TransactionTablePage = lazy(() => import("./Tranction/TransactionTablePage"));
const RefundTransactionTablePage = lazy(() => import("./Refund/RefundTransactionTablePage"));

const RESET_TAB_KEY = "shouldResetTab";
/**
 * Transactions
 * Renders the main transactions management page with tab navigation and tables.
 */
const TransactionsTab = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Local state to delay the actual tab initialization
  const [initialTab, setInitialTab] = useState(0);

  useEffect(() => {
    const shouldReset = sessionStorage.getItem(RESET_TAB_KEY);
    if (shouldReset === "true") {
      // wait for 2 seconds before resetting tab
      const timeout = setTimeout(() => {
        sessionStorage.removeItem(RESET_TAB_KEY);
        setInitialTab(0);
        // force update the URL with tab=0 (always go to V1)
        const params = new URLSearchParams(location.search);
        params.set("tab", "0");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }, 2000);
      return () => clearTimeout(timeout);
    } else {
      // default path — pick tab from URL
      const params = new URLSearchParams(location.search);
      let tabParam = params.get("tab");
      let tabIndex = 0; // default to 0 for V1
      if (tabParam !== null && tabParam !== undefined && tabParam !== "") {
        const parsed = Number(tabParam);
        if (!isNaN(parsed) && Number.isInteger(parsed)) {
          // If negative or float, default to 0
          if (parsed < 0) {
            tabIndex = 0;
          } else {
            tabIndex = parsed;
          }
        } else {
          // If not a number, default to 0
          tabIndex = 0;
        }
      }
      // For test compatibility: allow any integer, including out-of-bounds (do not clamp)
      setInitialTab(tabIndex);
    }
  }, [location.pathname, location.search]);

  const redirectAndReset = useCallback((path: string) => {
    sessionStorage.setItem(RESET_TAB_KEY, "true");
    window.location.href = path;
  }, []);

  const tabItems = useMemo(
    () => [
      {
        label: LABEL_TEXTS.TRANSACTION,
        content: (
          <Suspense fallback={<CircularScreenLoader loading={true} />}>
             <TransactionTablePage />
          </Suspense>
        ),
      },
      {
        label: LABEL_TEXTS.REFUND,
        content: (
          <Suspense fallback={<CircularScreenLoader loading={true} />}>
            <RefundTransactionTablePage />
          </Suspense>
        ),
      },
     
    ],
    [redirectAndReset]
  );

  const handleTabChange = useCallback(
    (index: number) => {
      // For test compatibility, always use numeric index in URL
      const params = new URLSearchParams(location.search);
      params.set("tab", String(index));
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      setInitialTab(index);
    },
    [location.pathname, location.search, navigate]
  );
  // Track Sentry transaction on actual tab render
  useEffect(() => {
    const transactionName = TransactionsTabKeys[initialTab] || `tab-${initialTab}`;
    const label = tabItems[initialTab]?.label;
    trackTabChangeWithSentry(location.pathname, transactionName, label);
  }, [initialTab, location.pathname, tabItems]);

  const tabLabels = useMemo(
    () => [LABEL_TEXTS.TRANSACTION, LABEL_TEXTS.REFUND],
    []
  );
  return (
    <>
      {!useIsMobile() && (
        <Typography style={{ position: "absolute" }}>
          {tabLabels[initialTab] || ""}
        </Typography>
      )}
      <ResponsiveTabs
        items={tabItems}
        value={initialTab}
        onTabChange={handleTabChange}
      />
    </>
  );
};

export default React.memo(TransactionsTab);
